import { getHasAnyProjects } from "@frontend-shared/utils";
import { memo, useCallback, useState } from "react";
import { Outlet } from "react-router-dom";

import { useNotificationSocket } from "@/components/notifications/hooks/use-notification-socket";
import { NotificationsDrawer } from "@/components/notifications/NotificationsDrawer";
import { NotificationsProvider } from "@/components/notifications/provider/NotificationsProvider";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { HeaderSlotContext } from "@/contexts/HeaderSlotContext";
import { useAuth } from "@/hooks/use-auth";
import { useFaviconBadge } from "@/hooks/use-favicon-badge";
import { useIsMeetingDetailsRoute } from "@/hooks/use-is-meeting-details-route";

import { CopyrightNotice } from "./CopyrightNotice";
import { GlobalSearch } from "./global-search/GlobalSearch";
import { useGlobalSearchShortcut } from "./global-search/hooks/use-global-search-shortcut";
import { Header } from "./Header";
import { MainContent } from "./MainContent";
import { MainSidebar } from "./MainSidebar";

const AppLayoutComponent = (): JSX.Element => {
  const [isSearchChatsOpen, setIsSearchChatsOpen] = useState(false);
  // NOTE: NSAI-5822 — the header's breadcrumb slot. Held as state rather than a
  // ref so the context re-renders its consumers once the element exists;
  // `Toolbar` portals into it from inside the routed page below.
  const [breadcrumbSlot, setBreadcrumbSlot] = useState<HTMLDivElement | null>(
    null,
  );
  const { projects, user } = useAuth();
  const isMeetingDetailsRoute = useIsMeetingDetailsRoute();

  useNotificationSocket({ isAuthenticated: Boolean(user) });

  // NOTE: Paints the unread count as a badge on top of the browser favicon
  // (alongside the "(N) " prefix on document.title from useDocumentTitle).
  // Mounted in the authenticated layout so unauthenticated pages don't try
  // to read the cache. Same data source as UserMenu's avatar badge.
  useFaviconBadge();

  const hasAnyProjects = getHasAnyProjects(projects);
  // NOTE: NSAI-6589 — the global search palette is there for every signed-in
  // user with a project; only its semantic message section still depends on a
  // search embedding model (see useGlobalSearchChats).
  const handleToggleSearch = useCallback((): void => {
    setIsSearchChatsOpen((isOpen) => !isOpen);
  }, []);
  useGlobalSearchShortcut({
    isEnabled: hasAnyProjects,
    onToggle: handleToggleSearch,
  });

  return (
    <NotificationsProvider>
      <SidebarProvider>
        {hasAnyProjects && (
          <GlobalSearch
            isOpen={isSearchChatsOpen}
            onOpenChange={setIsSearchChatsOpen}
          />
        )}
        <HeaderSlotContext.Provider value={breadcrumbSlot}>
          <div className="h-screen w-screen flex">
            {hasAnyProjects && <MainSidebar />}
            <SidebarInset>
              <Header
                isSearchChatsOpen={isSearchChatsOpen}
                setIsSearchChatsOpen={setIsSearchChatsOpen}
                breadcrumbSlotRef={setBreadcrumbSlot}
              />
              <MainContent>
                <Outlet />
              </MainContent>
              {/* NOTE: NSAI-7288 — where the footer bar's copyright used to sit,
                  without the bar: overlaid on the bottom-right corner, on the
                  same line as the chat disclaimer (12px up, 16px line), so it
                  costs the composer no height. Pages keep at least that much
                  bottom padding, so it lands in empty space — except the
                  meeting page, which ends in the recording play bar and its
                  speed and download controls, so it stands down there. Below
                  `lg` it would run into the centred disclaimer. Wherever it is
                  not shown, the account menu carries it instead. */}
              {!isMeetingDetailsRoute && (
                <CopyrightNotice
                  testId="app-copyright"
                  className="pointer-events-none absolute bottom-3 end-6 hidden leading-4 text-muted-foreground lg:block"
                />
              )}
            </SidebarInset>
          </div>
        </HeaderSlotContext.Provider>
        <NotificationsDrawer />
      </SidebarProvider>
    </NotificationsProvider>
  );
};

export const AppLayout = memo(AppLayoutComponent);
